import React, { useState } from "react";
import {
  Box,
  Flex,
  Text,
  IconButton,
} from "components-lib";
import { FaMicrophone, FaStop, FaPause, FaPlay } from 'react-icons/fa';

export function AllergiesVoiceSection({ medicines, food, other }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [status, setStatus] = useState('idle');
  const [spokenText, setSpokenText] = useState('');
  const [pausedText, setPausedText] = useState('');
  const cardBg = '#f7fafc';
  const borderColor = '#e2e8f0';
  const modernFont = `'Inter', 'Segoe UI', 'Arial', sans-serif`;

  const toggleRecording = () => {
    if (isRecording) {
      // Stop recording and simulate spoken text
      setIsRecording(false);
      setStatus('idle');
      const newText = 'Allergic to penicillin, shellfish, and dust mites';
      const finalText = pausedText ? `${pausedText} ${newText}` : newText;
      setSpokenText(finalText);
      setPausedText('');
      setIsPaused(false);
    } else if (isPaused) {
      // Resume recording
      setIsRecording(true);
      setIsPaused(false);
      setStatus('listening');
    } else {
      // Start recording
      setIsRecording(true);
      setStatus('listening');
      setSpokenText('');
    }
  };

  const pauseRecording = () => {
    if (isRecording) {
      setIsRecording(false);
      setIsPaused(true);
      setStatus('paused');
      const currentText = 'Allergic to penicillin';
      setPausedText(currentText);
      setSpokenText(currentText);
    }
  };

  return (
    <Box bg="white" rounded="lg" boxShadow="md" p={6} mb={6} borderWidth="1px" borderColor={borderColor} position="relative">
      <Text fontWeight="bold" fontSize="20px" mb={2} fontFamily={modernFont}>Allergies</Text>

      {/* COMMENTED OUT: Full allergy cards - showing only mic icon with functionality */}
      {/* <Flex direction={{ base: "column", md: "row" }} gap={4}>
        <Box flex={1} bg={cardBg} borderRadius="lg" p={6} borderWidth="1px" borderColor={borderColor} textAlign="center">
          <Text fontSize="md" color="gray.500" mb={1} fontFamily={modernFont} fontWeight="normal">Medicines</Text>
          <Text fontSize="2xl" fontWeight="bold" color="#222" fontFamily={modernFont}>
            {medicines && medicines.length > 0 && medicines[0] !== '' ? medicines.join(", ") : "--"}
          </Text>
        </Box>
        <Box flex={1} bg={cardBg} borderRadius="lg" p={6} borderWidth="1px" borderColor={borderColor} textAlign="center">
          <Text fontSize="md" color="gray.500" mb={1} fontFamily={modernFont} fontWeight="normal">Food</Text>
          <Text fontSize="2xl" fontWeight="bold" color="#222" fontFamily={modernFont}>
            {food && food.length > 0 && food[0] !== '' ? food.join(", ") : "--"}
          </Text>
        </Box>
        <Box flex={1} bg={cardBg} borderRadius="lg" p={6} borderWidth="1px" borderColor={borderColor} textAlign="center">
          <Text fontSize="md" color="gray.500" mb={1} fontFamily={modernFont} fontWeight="normal">Other</Text>
          <Text fontSize="2xl" fontWeight="bold" color="#222" fontFamily={modernFont}>
            {other && other.length > 0 && other[0] !== '' ? other.join(", ") : "--"}
          </Text>
        </Box>
      </Flex> */}

      {/* Show spoken text when stopped or paused */}
      {((isPaused || !isRecording) && spokenText) && (
        <Box
          bg="gray.50"
          rounded="md"
          p={4}
          mb={4}
          borderWidth="1px"
          borderColor="gray.200"
          minH="80px"
        >
          <Text fontSize="sm" color="gray.600" fontWeight="medium" mb={2}>
            {isPaused ? 'Paused Text:' : 'Spoken Text:'}
          </Text>
          <Text fontSize="md" color="gray.800" lineHeight="1.5">
            {spokenText}
          </Text>
        </Box>
      )}
      <Box mt={6} display="flex" justifyContent="center">
        <Flex align="center" gap={3}>
          <IconButton
            icon={isRecording ? <FaStop /> : <FaMicrophone />}
            colorScheme={isRecording ? 'red' : isPaused ? 'orange' : 'blue'}
            size="lg"
            isRound
            onClick={toggleRecording}
            animation={isRecording ? 'pulse 2s infinite' : 'none'}
            boxShadow="md"
            _hover={{ transform: 'scale(1.05)' }}
            _active={{ transform: 'scale(0.95)' }}
            aria-label="Voice Input"
            title={isPaused ? 'Resume Recording' : isRecording ? 'Stop Recording' : 'Start Recording'}
          />

          {/* Pause Button - only show when recording */}
          {isRecording && (
            <IconButton
              icon={<FaPause />}
              colorScheme="yellow"
              size="md"
              isRound
              onClick={pauseRecording}
              boxShadow="md"
              _hover={{ transform: 'scale(1.05)' }}
              _active={{ transform: 'scale(0.95)' }}
              aria-label="Pause Recording"
              title="Pause Recording"
            />
          )}

          {isRecording && (
            <Flex align="center" color="red.500" fontWeight="medium">
              <Box w={3} h={3} bg="red.500" rounded="full" mr={2} animation="pulse 2s infinite" />
              Recording...
            </Flex>
          )}
          {isPaused && (
            <Flex align="center" color="orange.500" fontWeight="medium">
              <Box w={3} h={3} bg="orange.500" rounded="full" mr={2} />
              Paused - Click mic to resume
            </Flex>
          )}
        </Flex>
      </Box>
      {status === 'listening' && (
        <Box mt={4} py={3} px={4} rounded="md" textAlign="center" bg="blue.50" color="blue.700" borderWidth="1px" borderColor="blue.200">
          Listening for allergies...
        </Box>
      )}
    </Box>
  );
}

export function ComplaintsVoiceSection({ complaints }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [status, setStatus] = useState('idle');
  const [spokenText, setSpokenText] = useState('');
  const [pausedText, setPausedText] = useState('');
  const cardBg = '#f7fafc';
  const borderColor = '#e2e8f0';
  const modernFont = `'Inter', 'Segoe UI', 'Arial', sans-serif`;

  const toggleRecording = () => {
    if (isRecording) {
      // Stop recording and simulate spoken text
      setIsRecording(false);
      setStatus('idle');
      const newText = 'severe headache for the past 3 days, moderate intensity, started after stress at work';
      const finalText = pausedText ? `${pausedText} ${newText}` : `Patient complains of ${newText}`;
      setSpokenText(finalText);
      setPausedText('');
      setIsPaused(false);
    } else if (isPaused) {
      // Resume recording
      setIsRecording(true);
      setIsPaused(false);
      setStatus('listening');
    } else {
      // Start recording
      setIsRecording(true);
      setStatus('listening');
      setSpokenText('');
    }
  };

  const pauseRecording = () => {
    if (isRecording) {
      setIsRecording(false);
      setIsPaused(true);
      setStatus('paused');
      const currentText = 'Patient complains of severe headache';
      setPausedText(currentText);
      setSpokenText(currentText);
    }
  };

  return (
    <Box bg="white" rounded="lg" boxShadow="md" p={6} mb={6} borderWidth="1px" borderColor={borderColor} position="relative">
      <Text fontWeight="bold" fontSize="20px" mb={2} fontFamily={modernFont}>Complaints</Text>

      {/* COMMENTED OUT: Full complaint cards - showing only mic icon with functionality */}
      {/* {complaints && complaints.length > 0 ? (
        complaints.map((item, idx) => (
          <Flex key={idx} direction={{ base: "column", md: "row" }} gap={4} mb={4}>
            <Box flex={1} bg={cardBg} borderRadius="lg" p={6} borderWidth="1px" borderColor={borderColor} textAlign="center">
              <Text fontSize="md" color="gray.500" mb={1} fontFamily={modernFont} fontWeight="normal">Complaint</Text>
              <Text fontSize="2xl" fontWeight="bold" color="#222" fontFamily={modernFont}>{item.complaint || '--'}</Text>
            </Box>
            <Box flex={1} bg={cardBg} borderRadius="lg" p={6} borderWidth="1px" borderColor={borderColor} textAlign="center">
              <Text fontSize="md" color="gray.500" mb={1} fontFamily={modernFont} fontWeight="normal">Severity</Text>
              <Text fontSize="2xl" fontWeight="bold" color="#222" fontFamily={modernFont}>{item.severity || '--'}</Text>
            </Box>
            <Box flex={1} bg={cardBg} borderRadius="lg" p={6} borderWidth="1px" borderColor={borderColor} textAlign="center">
              <Text fontSize="md" color="gray.500" mb={1} fontFamily={modernFont} fontWeight="normal">Duration</Text>
              <Text fontSize="2xl" fontWeight="bold" color="#222" fontFamily={modernFont}>{item.duration || '--'}</Text>
            </Box>
            <Box flex={1} bg={cardBg} borderRadius="lg" p={6} borderWidth="1px" borderColor={borderColor} textAlign="center">
              <Text fontSize="md" color="gray.500" mb={1} fontFamily={modernFont} fontWeight="normal">Date</Text>
              <Text fontSize="2xl" fontWeight="bold" color="#222" fontFamily={modernFont}>{item.date || '--'}</Text>
            </Box>
          </Flex>
        ))
      ) : (
        <Text color="gray.400" fontStyle="italic">No complaints added.</Text>
      )} */}

      {/* Show spoken text when stopped or paused */}
      {((isPaused || !isRecording) && spokenText) && (
        <Box
          bg="gray.50"
          rounded="md"
          p={4}
          mb={4}
          borderWidth="1px"
          borderColor="gray.200"
          minH="80px"
        >
          <Text fontSize="sm" color="gray.600" fontWeight="medium" mb={2}>
            {isPaused ? 'Paused Text:' : 'Spoken Text:'}
          </Text>
          <Text fontSize="md" color="gray.800" lineHeight="1.5">
            {spokenText}
          </Text>
        </Box>
      )}
      <Box mt={6} display="flex" justifyContent="center">
        <Flex align="center" gap={3}>
          <IconButton
            icon={isRecording ? <FaStop /> : <FaMicrophone />}
            colorScheme={isRecording ? 'red' : isPaused ? 'orange' : 'blue'}
            size="lg"
            isRound
            onClick={toggleRecording}
            animation={isRecording ? 'pulse 2s infinite' : 'none'}
            boxShadow="md"
            _hover={{ transform: 'scale(1.05)' }}
            _active={{ transform: 'scale(0.95)' }}
            aria-label="Voice Input"
            title={isPaused ? 'Resume Recording' : isRecording ? 'Stop Recording' : 'Start Recording'}
          />

          {/* Pause Button - only show when recording */}
          {isRecording && (
            <IconButton
              icon={<FaPause />}
              colorScheme="yellow"
              size="md"
              isRound
              onClick={pauseRecording}
              boxShadow="md"
              _hover={{ transform: 'scale(1.05)' }}
              _active={{ transform: 'scale(0.95)' }}
              aria-label="Pause Recording"
              title="Pause Recording"
            />
          )}

          {isRecording && (
            <Flex align="center" color="red.500" fontWeight="medium">
              <Box w={3} h={3} bg="red.500" rounded="full" mr={2} animation="pulse 2s infinite" />
              Recording...
            </Flex>
          )}
          {isPaused && (
            <Flex align="center" color="orange.500" fontWeight="medium">
              <Box w={3} h={3} bg="orange.500" rounded="full" mr={2} />
              Paused - Click mic to resume
            </Flex>
          )}
        </Flex>
      </Box>
      {status === 'listening' && (
        <Box mt={4} py={3} px={4} rounded="md" textAlign="center" bg="blue.50" color="blue.700" borderWidth="1px" borderColor="blue.200">
          Listening for complaints...
        </Box>
      )}
    </Box>
  );
}

export function DiagnosisVoiceSection({ diagnosisList }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [status, setStatus] = useState('idle');
  const [spokenText, setSpokenText] = useState('');
  const [pausedText, setPausedText] = useState('');
  const cardBg = '#f7fafc';
  const borderColor = '#e2e8f0';
  const modernFont = `'Inter', 'Segoe UI', 'Arial', sans-serif`;

  const toggleRecording = () => {
    if (isRecording) {
      // Stop recording and simulate spoken text
      setIsRecording(false);
      setStatus('idle');
      const newText = 'likely due to stress and poor sleep patterns, duration approximately one week';
      const finalText = pausedText ? `${pausedText} ${newText}` : `Primary diagnosis is tension headache, ${newText}`;
      setSpokenText(finalText);
      setPausedText('');
      setIsPaused(false);
    } else if (isPaused) {
      // Resume recording
      setIsRecording(true);
      setIsPaused(false);
      setStatus('listening');
    } else {
      // Start recording
      setIsRecording(true);
      setStatus('listening');
      setSpokenText('');
    }
  };

  const pauseRecording = () => {
    if (isRecording) {
      setIsRecording(false);
      setIsPaused(true);
      setStatus('paused');
      const currentText = 'Primary diagnosis is tension headache';
      setPausedText(currentText);
      setSpokenText(currentText);
    }
  };

  return (
    <Box bg="white" rounded="lg" boxShadow="md" p={6} mb={6} borderWidth="1px" borderColor={borderColor} position="relative">
      <Text fontWeight="bold" fontSize="20px" mb={2} fontFamily={modernFont}>Diagnosis</Text>

      {/* COMMENTED OUT: Full diagnosis cards - showing only mic icon with functionality */}
      {/* {diagnosisList && diagnosisList.length > 0 ? (
        diagnosisList.map((item, idx) => (
          <Flex key={idx} direction={{ base: "column", md: "row" }} gap={4} mb={4}>
            <Box flex={1} bg={cardBg} borderRadius="lg" p={6} borderWidth="1px" borderColor={borderColor} textAlign="center">
              <Text fontSize="md" color="gray.500" mb={1} fontFamily={modernFont} fontWeight="normal">Diagnosis</Text>
              <Text fontSize="2xl" fontWeight="bold" color="#222" fontFamily={modernFont}>{item.diagnosis || '--'}</Text>
            </Box>
            <Box flex={1} bg={cardBg} borderRadius="lg" p={6} borderWidth="1px" borderColor={borderColor} textAlign="center">
              <Text fontSize="md" color="gray.500" mb={1} fontFamily={modernFont} fontWeight="normal">Duration</Text>
              <Text fontSize="2xl" fontWeight="bold" color="#222" fontFamily={modernFont}>{item.duration || '--'}</Text>
            </Box>
            <Box flex={1} bg={cardBg} borderRadius="lg" p={6} borderWidth="1px" borderColor={borderColor} textAlign="center">
              <Text fontSize="md" color="gray.500" mb={1} fontFamily={modernFont} fontWeight="normal">Date</Text>
              <Text fontSize="2xl" fontWeight="bold" color="#222" fontFamily={modernFont}>{item.date || '--'}</Text>
            </Box>
          </Flex>
        ))
      ) : (
        <Text color="gray.400" fontStyle="italic">No diagnosis added.</Text>
      )} */}

      {/* Show spoken text when stopped or paused */}
      {((isPaused || !isRecording) && spokenText) && (
        <Box
          bg="gray.50"
          rounded="md"
          p={4}
          mb={4}
          borderWidth="1px"
          borderColor="gray.200"
          minH="80px"
        >
          <Text fontSize="sm" color="gray.600" fontWeight="medium" mb={2}>
            {isPaused ? 'Paused Text:' : 'Spoken Text:'}
          </Text>
          <Text fontSize="md" color="gray.800" lineHeight="1.5">
            {spokenText}
          </Text>
        </Box>
      )}
      <Box mt={6} display="flex" justifyContent="center">
        <Flex align="center" gap={3}>
          <IconButton
            icon={isRecording ? <FaStop /> : <FaMicrophone />}
            colorScheme={isRecording ? 'red' : isPaused ? 'orange' : 'blue'}
            size="lg"
            isRound
            onClick={toggleRecording}
            animation={isRecording ? 'pulse 2s infinite' : 'none'}
            boxShadow="md"
            _hover={{ transform: 'scale(1.05)' }}
            _active={{ transform: 'scale(0.95)' }}
            aria-label="Voice Input"
            title={isPaused ? 'Resume Recording' : isRecording ? 'Stop Recording' : 'Start Recording'}
          />

          {/* Pause Button - only show when recording */}
          {isRecording && (
            <IconButton
              icon={<FaPause />}
              colorScheme="yellow"
              size="md"
              isRound
              onClick={pauseRecording}
              boxShadow="md"
              _hover={{ transform: 'scale(1.05)' }}
              _active={{ transform: 'scale(0.95)' }}
              aria-label="Pause Recording"
              title="Pause Recording"
            />
          )}

          {isRecording && (
            <Flex align="center" color="red.500" fontWeight="medium">
              <Box w={3} h={3} bg="red.500" rounded="full" mr={2} animation="pulse 2s infinite" />
              Recording...
            </Flex>
          )}
          {isPaused && (
            <Flex align="center" color="orange.500" fontWeight="medium">
              <Box w={3} h={3} bg="orange.500" rounded="full" mr={2} />
              Paused - Click mic to resume
            </Flex>
          )}
        </Flex>
      </Box>
      {status === 'listening' && (
        <Box mt={4} py={3} px={4} rounded="md" textAlign="center" bg="blue.50" color="blue.700" borderWidth="1px" borderColor="blue.200">
          Listening for diagnosis...
        </Box>
      )}
    </Box>
  );
} 